import React, { useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, Grid, Chip, Divider,
} from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import { format } from 'date-fns';
import useDmsPalette from '../hooks/useDmsPalette';
import ClassificationBadge from './ClassificationBadge';

function formatFileSize(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Simple set-based word diff — good enough for "simple word-diff" requirement. */
function computeWordDiff(text1, text2) {
  const words1 = (text1 || '').trim().split(/\s+/).filter(Boolean);
  const words2 = (text2 || '').trim().split(/\s+/).filter(Boolean);
  const set2 = new Set(words2);
  const set1 = new Set(words1);

  const side1 = words1.map((w) => ({ word: w, type: set2.has(w) ? 'same' : 'deleted' }));
  const side2 = words2.map((w) => ({ word: w, type: set1.has(w) ? 'same' : 'added' }));
  return { side1, side2 };
}

function DiffText({ words }) {
  const MP = useDmsPalette();
  return (
    <Box sx={{
      fontFamily: 'monospace', fontSize: '0.78rem', lineHeight: 1.9,
      p: 1.5, bgcolor: MP.deepBlue, borderRadius: 1,
      maxHeight: 280, overflowY: 'auto',
      border: `1px solid ${MP.border}`,
    }}>
      {words.length === 0 ? (
        <Typography variant="caption" color="text.secondary">(empty)</Typography>
      ) : (
        words.map((item, i) => (
          <span
            key={i}
            style={{
              backgroundColor:
                item.type === 'deleted' ? 'rgba(244,67,54,0.22)'
                  : item.type === 'added' ? 'rgba(76,175,80,0.22)'
                    : 'transparent',
              textDecoration: item.type === 'deleted' ? 'line-through' : 'none',
              color:
                item.type === 'deleted' ? '#ef9a9a'
                  : item.type === 'added' ? '#a5d6a7'
                    : MP.white,
              padding: '1px 3px',
              borderRadius: 2,
              marginRight: 2,
            }}
          >
            {item.word}
          </span>
        ))
      )}
    </Box>
  );
}

function VersionMeta({ version, label }) {
  const MP = useDmsPalette();
  if (!version) return null;
  return (
    <Box sx={{ p: 1.5, bgcolor: MP.deepBlue, borderRadius: 1, border: `1px solid ${MP.border}`, height: '100%' }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Chip
        label={`v${version.version || version.versionNumber || '?'}`}
        size="small"
        sx={{ fontWeight: 700, mb: 1, bgcolor: `${MP.blue}30`, color: MP.blue, fontSize: '0.75rem' }}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          <strong>Date:</strong>{' '}
          {version.createdAt ? format(new Date(version.createdAt), 'MMM d, yyyy · h:mm a') : '—'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          <strong>Size:</strong> {formatFileSize(version.fileSize || version.size)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          <strong>By:</strong>{' '}
          {version.changedBy?.name || version.uploadedBy?.name || version.createdBy?.name || 'Unknown'}
        </Typography>
        {version.classification && (
          <Box sx={{ mt: 0.5 }}>
            <ClassificationBadge classification={version.classification} />
          </Box>
        )}
        {version.changelog && (
          <Typography variant="caption" sx={{ color: MP.white, mt: 0.5, display: 'block', fontStyle: 'italic' }}>
            "{version.changelog}"
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export default function VersionDiff({ version1, version2, onClose }) {
  const MP = useDmsPalette();
  const open = Boolean(version1 && version2);

  const text1 = version1?.extractedText || version1?.content || '';
  const text2 = version2?.extractedText || version2?.content || '';
  const hasBothTexts = Boolean(text1 && text2);

  const { side1, side2 } = useMemo(() => {
    if (!hasBothTexts) return { side1: [], side2: [] };
    return computeWordDiff(text1, text2);
  }, [text1, text2, hasBothTexts]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CompareArrowsIcon sx={{ color: MP.blue }} />
        Version Comparison
      </DialogTitle>

      <DialogContent>
        {/* Metadata side by side */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <VersionMeta version={version1} label="Version A (older)" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <VersionMeta version={version2} label="Version B (current)" />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2, borderColor: MP.border }} />

        {/* Text diff section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
            Text Comparison
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, ml: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: 'rgba(244,67,54,0.5)' }} />
              <Typography variant="caption" color="text.secondary">Removed (in A, not B)</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: 'rgba(76,175,80,0.5)' }} />
              <Typography variant="caption" color="text.secondary">Added (in B, not A)</Typography>
            </Box>
          </Box>
        </Box>

        {hasBothTexts ? (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
                Version A
              </Typography>
              <DiffText words={side1} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
                Version B (current)
              </Typography>
              <DiffText words={side2} />
            </Grid>
          </Grid>
        ) : (
          <Box sx={{
            textAlign: 'center', py: 5,
            bgcolor: MP.deepBlue, borderRadius: 1,
            border: `1px dashed ${MP.border}`,
          }}>
            <CompareArrowsIcon sx={{ fontSize: 40, color: MP.textSecondary, mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Binary file — text comparison not available, showing metadata only.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
